
import * as React from 'react';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { resetItems } from '../../operations/resetItems';
import strings from '../../models/error/ErrorStrings.resx';
import * as styles from './ItemError.scss';
import { Omit } from '../../utilities/Omit';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { IWithLayoutRenderCompleteProps, WithLayoutRenderComplete } from '../withLayoutRenderComplete/WithLayoutRenderComplete';
import { itemErrorControl, IItemErrorRendererProps } from '../../controls/itemErrorControl';
import { errorFacet } from '../../facets/DemandFacets';
import { withResponsiveMode, IResponsiveModeProps, ResponsiveMode } from '../decorators/withResponsiveMode';
import { ItemOperationError } from '../../store/actions/implementOperation';
import { unpack } from '../../models/Facet';
import { accessDeniedErrorFacet } from '../../facets/ErrorFacets';

// Number of available images for errors from the CDN.
const IMAGE_COUNT = 3;

type ErrorImageSize = 'small' | 'large';

const sizePixelMapping: { [K in ErrorImageSize]: number } = {
    small: 140,
    large: 208
};

/**
 * State for the ErrorBase component.
 * @internal
 */
export interface IErrorBaseState {
    /**
     * The selected ordinal for the error image to display.
     */
    errorImageNum: number;
}

/**
 * Props used when rendering an error.
 * @public
 */
export interface IErrorBaseProps {
    /**
     * The underlying error from the system.
     */
    error: Error;
    /**
     * The title or broad category of the error.
     * Must be localized.
     */
    title?: string;
    /**
     * The default subtext extracted for the error.
     */
    subText?: string;
    /**
     * The url to the image to be displayed on the error page
     */
    errorImageUrl?: string;
    /**
     * Error detailed message element
     */
    errorDetailsContent?: React.ReactNode;
    /**
     * A set of buttons for actions the user may take to remedy the error.
     */
    actions?: React.ReactNode;
    /**
     * A callback to invoke once the error mounts.
     */
    onError?: () => void;
}

/**
 * Component which renders an error loading data to the user in a friendly manner.
 * @public
 */
export const ErrorBase: React.ComponentType<IErrorBaseProps> = withResponsiveMode()(
    class extends React.PureComponent<IItemErrorRendererProps & IResponsiveModeProps> {
        public componentDidMount(): void {
            const {
                onError
            } = this.props;

            if (onError) {
                onError();
            }
        }

        public render(): JSX.Element | null {
            const {
                error,
                title,
                subText,
                errorImageUrl,
                errorDetailsContent,
                actions,
                responsiveMode
            } = this.props;

            const errorImageSize: ErrorImageSize = responsiveMode ? responsiveMode > ResponsiveMode.large ? 'large' : 'small' : 'large';

            const pixelSize = sizePixelMapping[errorImageSize];

            return error ? (
                <div className={ styles.itemError } >
                    {
                        errorImageUrl ? (
                            <div
                                className={ styles.itemErrorImageContainer }
                                style={ { height: pixelSize + 'px', width: pixelSize + 'px' } }
                            >
                                <img className={ styles.itemErrorImage } src={ errorImageUrl } />
                            </div>
                        ) : null
                    }
                    <div className={ styles.itemErrorTitle }>
                        { title || strings.DefaultTitle }
                    </div>
                    { errorDetailsContent ?
                        (
                            <div className={ styles.itemErrorMessage }>
                                { errorDetailsContent }
                            </div>
                        ) : (
                            <div className={ styles.itemErrorMessage }>
                                { subText || error.message || strings.DefaultSubText }
                            </div>
                        )
                    }
                    { actions ?
                        (
                            <div className={ styles.itemErrorActions }>
                                { actions }
                            </div>
                        ) : null
                    }
                </div>
            ) : null;
        }
    });

interface IConnectedItemErrorProps {
    itemKey: string;
    error: Error;
}

class ConnectedItemError extends React.PureComponent<IConnectedItemErrorProps, IErrorBaseState> {
    constructor(props: IConnectedItemErrorProps) {
        super(props);

        this.state = {
            errorImageNum: Math.floor(Math.random() * IMAGE_COUNT) + 1
        };
    }

    public render(): JSX.Element {
        const {
            itemKey,
            error
        } = this.props;

        /* tslint:disable-next-line:max-line-length */
        const errorUrl = `https://static2.sharepointonline.com/files/fabric/office-ui-fabric-react-assets/images/error/error${this.state.errorImageNum}.svg`;

        return (
            <WithItemCache>
                { ({ itemCache, dispatch }: IItemCacheStateProps) => {
                    const controlHandler = itemCache.demandItemFacet(itemErrorControl, itemKey);

                    const {
                        errorRenderer: ItemErrorRenderer,
                        onError
                    } = itemErrorControl.evaluate(controlHandler)(itemCache, {
                        itemKey: itemKey,
                        error: error,
                        errorRenderer: ErrorBase
                    });

                    const onClickRetry = () => {
                        dispatch(resetItems({
                            items: {
                                [itemKey]: true
                            }
                        }));
                    };

                    const onErrorMounted = onError ? () => {
                        dispatch(onError({
                            itemKey,
                            error
                        }));
                    } : undefined;

                    let title: string | undefined;
                    let subText: string | undefined;

                    if (error instanceof ItemOperationError) {
                        const {
                            accessDeniedError
                        } = unpack({
                            accessDeniedError: accessDeniedErrorFacet
                        }, error.facets);

                        if (accessDeniedError) {
                            title = strings.ItemNotFoundTitle;
                            subText = strings.ItemNotFoundSubText;
                        }
                    }

                    return (
                        <ItemErrorRenderer
                            error={ error }
                            itemKey={ itemKey }
                            onError={ onErrorMounted }
                            title={ title }
                            subText={ subText }
                            errorImageUrl={ errorUrl }
                            actions={
                                (
                                    <DefaultButton text={ strings.retryButtonLabel } onClick={ onClickRetry } />
                                )
                            }
                        />
                    );
                } }
            </WithItemCache>
        );
    }
}

/**
 * Props for the item error renderer.
 * @public
 */
export type IItemErrorProps = Omit<IWithLayoutRenderCompleteProps, 'isDataReadyForLayout'> & {
    itemKey: string;
};

/**
 * Component which renders an error which occurred when an item failed to load.
 * @public
 */
export class ItemError extends React.PureComponent<IItemErrorProps> {
    public render(): JSX.Element | null {
        const {
            itemKey,
            onLayoutRenderComplete
        } = this.props;

        return (
            <WithItemCache>
                {
                    ({ itemCache, dispatch }: IItemCacheStateProps): JSX.Element | null => {
                        const isError = itemCache.isItemFacetResolved(errorFacet, itemKey);

                        const error = isError ? itemCache.demandItemFacet(errorFacet, itemKey) : undefined;

                        if (!error) {
                            return null;
                        }

                        return (
                            <WithLayoutRenderComplete isDataReadyForLayout={ isError } onLayoutRenderComplete={ onLayoutRenderComplete }>
                                <ConnectedItemError itemKey={ itemKey } error={ error } />
                            </WithLayoutRenderComplete>
                        );
                    }
                }
            </WithItemCache>
        );
    }
}
